// /docs — single-page docs with anchor sections. Enough to orient a
// first-time user, link-to-able for support convos. The real deep-docs
// move to a separate site (docs.malbox.neels.dev) later; this page is
// the public entry.
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks";
import { MarketingShell } from "./MarketingShell";

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-rule-row pt-8 first:border-t-0 first:pt-0">
      <h2
        className="font-mono text-ink"
        style={{ fontSize: "clamp(22px, 2.2vw, 28px)", letterSpacing: "-0.02em" }}
      >
        {title}
      </h2>
      <div className="mt-4 max-w-[720px] space-y-4 text-ui text-ink-soft">{children}</div>
    </section>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <pre className="tscroll overflow-x-auto border border-rule-row bg-paper p-3 font-mono text-[12.5px] leading-[1.5] text-ink">
      {children}
    </pre>
  );
}

export function DocsPage() {
  useSeo({
    title: "Docs — malbox malware sandbox",
    description: "How to submit a malware sample, interact with a running analysis, read the report, use the REST API, and self-host malbox. Quickstart for malware analysts and SOC engineers.",
    path: "/docs",
    keywords: "malware sandbox docs, malware sandbox API, submit malware sample, malware analysis quickstart, self-host malware sandbox, YARA API, Suricata sandbox API, malware triage docs",
  });
  return (
    <MarketingShell
      eyebrow="Docs"
      title="Everything you need to run a sample."
      lede={<>Browser flow, API, and self-hosting. Start at Quickstart; jump to Self-host if you want the whole stack on your own hardware.</>}
    >
      <nav className="mb-10 flex flex-wrap gap-x-5 gap-y-2 border-y border-rule-row py-3 text-ui text-ink-soft">
        <a href="#quickstart" className="hover:text-ink hover:underline">Quickstart</a>
        <a href="#interact" className="hover:text-ink hover:underline">Interact with a run</a>
        <a href="#report" className="hover:text-ink hover:underline">Read a report</a>
        <a href="#api" className="hover:text-ink hover:underline">API</a>
        <a href="#self-host" className="hover:text-ink hover:underline">Self-host</a>
        <a href="#limits" className="hover:text-ink hover:underline">Limits</a>
      </nav>

      <div className="space-y-12">
        <Section id="quickstart" title="Quickstart">
          <p>
            Open <Link to="/submit" viewTransition className="text-ink underline">/submit</Link>. Drop a Windows PE, paste a URL, or upload an archive (zip, 7z, rar — password-protected or not; 'infected' is tried automatically).
          </p>
          <p>
            Pick a profile (Windows 10 / 11), timeout, and network mode (internet, isolated, or simulated). Click Submit. You'll land on the report page as the analysis streams in.
          </p>
          <p>
            First run cold-boots a VM (~90 s) + runs the sample for your chosen timeout + collects artifacts to S3. The report page shows live telemetry, screenshots, and a VNC window if you want to interact.
          </p>
        </Section>

        <Section id="interact" title="Interact with a run">
          <p>
            On a running job's report, open the <b className="font-medium text-ink">Screen</b> panel for a live VNC view. Enable <b className="font-medium text-ink">Interactive input</b> (requires sign-in — click gates control to the submitter) and you can click, type, and dismiss dialogs. The sample sees your input in real time.
          </p>
          <p>
            Interaction is scoped to the current slot; closing the tab doesn't stop the run. Return any time before the timeout.
          </p>
        </Section>

        <Section id="report" title="Read a report">
          <p>Every completed job has:</p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li><b className="font-medium text-ink">Verdict + score</b> — benign / suspicious / malicious, 0-100, with max severity</li>
            <li><b className="font-medium text-ink">Behavior tags</b> — MITRE ATT&CK-mapped (execution.dropped-then-exec, injection.remote-thread, …)</li>
            <li><b className="font-medium text-ink">Processes</b> — tree, command lines, image loads, thread injections</li>
            <li><b className="font-medium text-ink">Network</b> — hosts, flows, HTTP (decrypted via MITM), DNS, Suricata badges on each row, hex view per flow</li>
            <li><b className="font-medium text-ink">Config</b> — extracted RAT/stealer configs (C2, mutex, keys, campaign, raw dump)</li>
            <li><b className="font-medium text-ink">Static PE</b> — sections, imports, strings, imphash</li>
            <li><b className="font-medium text-ink">Artifacts</b> — pcap, memdump, replay, screenshots — all downloadable</li>
            <li><b className="font-medium text-ink">Third-party intel</b> — joined VirusTotal / Triage / Hybrid Analysis / MalwareBazaar / YARAify / MalShare</li>
          </ul>
        </Section>

        <Section id="api" title="API">
          <p>
            REST under <code className="font-mono text-ink">/v1/</code>. Same backend the UI uses. The read endpoints (jobs, events, artifacts) are open during beta. Programmatic submission requires an API key — email <a href="mailto:neel@neels.dev?subject=malbox%20API%20access" className="text-ink underline">neel@neels.dev</a> for one.
          </p>
          <p className="text-ink">Submit a sample (presigned S3 upload — bypasses the 100 MB Cloudflare cap):</p>
          <Code>{`# 1. Compute SHA256 of the sample
sha256sum sample.exe

# 2. Get a presigned PUT URL (requires API key header)
curl -X POST https://malbox.neels.dev/v1/jobs/presign \\
  -H 'authorization: Bearer <api-key>' \\
  -H 'content-type: application/json' \\
  -d '{"sha256":"<sha>","filename":"sample.exe","size":<bytes>}'

# 3. PUT the bytes directly to S3
curl -X PUT --data-binary @sample.exe '<put_url_from_step_2>'

# 4. Finalize the submission (creates the job row)
curl -X POST https://malbox.neels.dev/v1/jobs/finalize \\
  -H 'authorization: Bearer <api-key>' \\
  -H 'content-type: application/json' \\
  -d '{"sha256":"<sha>","filename":"sample.exe","spec":{"profile":"windows-10-22h2","network":"internet","timeout_secs":90}}'`}</Code>
          <p>For files &gt; 80 MB use <code className="font-mono text-ink">/v1/jobs/presign-multipart</code> + per-part PUT + <code className="font-mono text-ink">/v1/jobs/finalize-multipart</code>. For archives that should expand into N jobs use <code className="font-mono text-ink">/v1/jobs/finalize-archive</code>.</p>
          <p className="text-ink">Poll a job's events:</p>
          <Code>{`curl 'https://malbox.neels.dev/v1/jobs/<id>/events?since=0'`}</Code>
          <p>Returns <code className="font-mono text-ink">{`{events, next_offset, is_terminal}`}</code>. Up to 2000 events per page; call again with <code className="font-mono text-ink">since=next_offset</code> until <code className="font-mono text-ink">is_terminal && events.length === 0</code>.</p>
        </Section>

        <Section id="self-host" title="Self-host">
          <p>
            Self-hosted deployments are coming soon. The whole stack is a Nix flake — one <code className="font-mono text-ink">nixos-rebuild switch</code> per host — and will be opened to early-access users first.
          </p>
          <p>
            Interested in running malbox on your own hardware (air-gapped lab, enterprise fleet, dedicated hardware)? Email <a href="mailto:neel@neels.dev?subject=Self-hosted%20malbox" className="text-ink underline">neel@neels.dev</a> with your use case and we'll loop you into the private access list.
          </p>
        </Section>

        <Section id="limits" title="Limits (beta)">
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li><b className="font-medium text-ink">Sample size</b>: 512 MiB per file. Archives up to 1 GiB.</li>
            <li><b className="font-medium text-ink">Run timeout</b>: 30 s minimum, 30 min maximum. Default 90 s.</li>
            <li><b className="font-medium text-ink">Bulk submission</b>: 5 files per submission. Archives expand server-side with no per-file limit.</li>
            <li><b className="font-medium text-ink">Signed-in bulk</b>: 2 bulk submissions per rolling 24h window per account.</li>
            <li><b className="font-medium text-ink">OSes</b>: Windows 10 / 11 only. ELF / Mach-O / Linux scripts not supported.</li>
          </ul>
        </Section>
      </div>
    </MarketingShell>
  );
}
